import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InstancesService } from 'src/app/services/api/instances.service';
import { PortalStateService } from '../../services/portal-state.service';
import { combineLatest } from 'rxjs';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { SolutionVersionsService } from 'src/app/services/util/solution-versions.service';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';

type TabId = 'overview' | 'endpoints' | 'release-packages' | 'rvt' | 'live-monitoring';

export type PathToLiveStageStatus = 'Active' | 'Failed';

export function resolveLifecycleStatus(lifecycleState?: string): PathToLiveStageStatus | undefined {
  switch (lifecycleState) {
    case 'success': return 'Active';
    case 'failed': return 'Failed';
    default: return undefined;
  }
}

interface EnvBanner {
  name: string;
  instanceName?: string;
  class: string;
  status?: PathToLiveStageStatus;
  blueprint: string;
}

@Component({
  selector: 'app-environment-details',
  templateUrl: './environment-details.component.html',
  styleUrl: './environment-details.component.scss',
  standalone: false
})
export class EnvironmentDetailsComponent implements OnInit {
  @Input() envId!: string;

  private destroyRef = inject(DestroyRef);
  private instancesService = inject(InstancesService);
  private portalState = inject(PortalStateService);
  private solutionsService = inject(SolutionsService);
  private solutionVersionsService = inject(SolutionVersionsService);
  private blueprintsService = inject(BlueprintsService);

  readonly statusStyle: Record<PathToLiveStageStatus, string> = {
    'Active': 'status-active',
    'Failed': 'status-failed',
  };

  readonly statusLabels: Record<PathToLiveStageStatus, string> = {
    'Active': 'FEATURES.SERVICE_DEFINITION_DETAILS.ACTIVE',
    'Failed': 'FEATURES.SERVICE_DEFINITION_DETAILS.FAILED',
  };

  tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: 'language' },
    { id: 'endpoints', label: 'Endpoints', icon: 'desktop_windows' },
    { id: 'release-packages', label: 'Release Packages', icon: 'inventory_2' },
    { id: 'live-monitoring', label: 'Live Monitoring', icon: 'donut_large' }
  ];
  activeTab = signal<TabId>('overview');

  env = signal<EnvBanner | null>(null);
  instance = signal<any | null>(null);
  loading = signal(true);
  error = signal(false);
  solutionName = signal<string | undefined>(undefined);
  releaseVersion = signal<string | undefined>(undefined);
  isAMR = signal(false);
  solutionLoading = signal(true);
  instanceLoading = signal(true);
  stageLoading = signal(false);
  headerLoading = computed(() => this.instanceLoading() || this.stageLoading());
  blueprintInfo = signal<{ dateCreated?: string; dateUpdated?: string; modifiedBy?:string } | null>(null);
  overviewInstance = computed(() => this.instance() ?? this.blueprintInfo());

  ngOnInit(): void {
    const state = this.portalState.getCurrentStateSnapshot();
    const stage: any = state.envStage;
    this.envId = stage?.envId || state.envId || this.envId || '';
    const envName = stage?.environment ?? state.envName ?? '';
    const blueprint = stage?.blueprintName ?? state.serviceName ?? '';
    if (stage) {
      this.env.set({
        name: envName,
        class: stage.deploymentClass ?? '',
        status: resolveLifecycleStatus(stage.lifecycleState),
        blueprint
      });
    } else if (envName || blueprint) {
      this.env.set({ name: envName, class: '', blueprint });
    }
    const noInstance = stage
      ? (stage.hasInstance === false || stage.envId === stage.environment)
      : (!!this.envId && this.envId === envName);
    if (noInstance) {
      this.loading.set(false);
      this.instanceLoading.set(false);  
      this.stageLoading.set(true);      
      this.solutionLoading.set(true);    
      this.loadPathToLiveStage(
        envName,
        blueprint,
        stage?.blueprint?.id,
        (state as any).serviceId
      );
      return;
    }
    if (!this.envId) {
      this.loading.set(false);
      this.instanceLoading.set(false);
      this.solutionLoading.set(false);
      if (!this.env()) { this.error.set(true); }
      return;
    }
    this.loadEnvironment(this.envId);
  }

  private loadSolutionInfo(instance: any): void {
    const solutionId = instance?.solutionId;
    if (!solutionId) { this.solutionLoading.set(false); return; }

    combineLatest([
      this.solutionsService.getSolutionDetailsById(solutionId),
      this.solutionsService.getSolutionVersions(solutionId)
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([solution, versions]: any[]) => {
          this.solutionName.set(solution?.name);
          const v = versions?.find((x: any) => x.id === instance.solutionVersionId);
          this.releaseVersion.set(v?.releaseVersion);
          this.isAMR.set(!!v?.releaseVersion &&
            !!this.solutionVersionsService.releaseVersion(v.releaseVersion));
          this.solutionLoading.set(false);
        },
        error: () => this.solutionLoading.set(false)
      });
  }

  private loadPathToLiveStage(envName: string, blueprintName: string, blueprintId?: string, serviceId?: string): void {
    const blueprints$ = blueprintId
      ? this.blueprintsService.getBlueprint(blueprintId).pipe(map((bp: any) => [bp]))
      : serviceId
        ? this.blueprintsService.getBlueprintsForSolution(serviceId)
        : of([] as any[]);

    blueprints$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blueprints: any[]) => {
          const bp = (blueprints ?? []).find(b => blueprintId ? b?.id === blueprintId : b?.name === blueprintName);
          const cfg = bp?.pathToLiveConfiguration?.find((p: any) => p?.environment === envName);
          const cur = this.env();
          if (cfg && cur) {
            this.env.set({ ...cur, class: cfg.deploymentClass ?? cur.class });
          }
          if (bp) {
            this.blueprintInfo.set({ dateCreated: bp.dateCreated, dateUpdated: bp.dateUpdated, modifiedBy: bp.modifiedBy });
            if (bp.solutionId) {
              this.solutionsService.getSolutionDetailsById(bp.solutionId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                  next: (s: any) => { this.solutionName.set(s?.name); this.solutionLoading.set(false); },
                  error: () => this.solutionLoading.set(false)
                });
            } else {
              this.solutionLoading.set(false);
            }
          } else {
            this.solutionLoading.set(false);
          }
          this.stageLoading.set(false);
        },
        error: () => {
          this.stageLoading.set(false);
          this.solutionLoading.set(false);
        }

      });
  }

  private loadEnvironment(id: string): void {
    this.instancesService.getInstanceById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (instance: any) => {
          this.instanceLoading.set(false);
          if (!instance || Number(instance.opstatus) > 0 || instance.errmsg) {
            this.solutionLoading.set(false);
            this.loading.set(false);
            return;
          }
          this.instance.set(instance);
          this.loadSolutionInfo(instance);
          const current = this.env();
          this.env.set({
            name: current?.name ?? instance.environment ?? '-',
            instanceName: instance.name,
            class: instance.deploymentClass || current?.class || '-',
            status: resolveLifecycleStatus(instance.lifecycleState) ?? current?.status,
            blueprint: current?.blueprint ?? '-'
          });
          this.loading.set(false);
        },
        error: () => {
          this.instanceLoading.set(false);
          this.solutionLoading.set(false);
          if (!this.env()) { this.error.set(true); }
          this.loading.set(false);
        }
      });
  }
  
  selectTab(id: TabId) { this.activeTab.set(id); }
}